import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Services
import { DASHBOARD_QUERY_KEY, useDashboardActionMutation } from '../../services/dashboard/dashboardQueries';

// Components
import useToast from '../../components/molecules/Toast/useToast';

// Utils
import { normalizeUrl } from '../../utils/url';

/**
 * Hook to run dashboard item actions: optional confirm, then POST + toast + reload, or open a page
 * @returns {useDashboardActionReturn} Action state and handlers
 */
const useDashboardAction = () => {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState(/** @type {DashboardActionFlattened | null} */ (null));
  const [confirmIsOpen, setConfirmIsOpen] = useState(false);

  const closeConfirm = useCallback(() => {
    setConfirmIsOpen(false);
    setPendingAction(null);
  }, []);

  const {
    mutate,
    isPending,
    variables,
  } = useDashboardActionMutation({
    onSuccess: (data) => {
      if (data?.message) {
        notify({
          message: data.message,
          type: data.success ? 'success' : 'alert',
        });
      }
      if (data?.reload) {
        queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEY], type: 'active' });
      }
      closeConfirm();
    },
    onError: (error) => {
      notify({
        message: error?.message || t('dashboard.actions.error'),
        type: 'alert',
      });
    },
  });

  const executeAction = useCallback((/** @type {DashboardActionFlattened} */ action) => {
    switch (action.type) {
      case 'reload': {
        const { actionUrl } = /** @type {ApiDashboardReloadAction} */ (action);
        if (!actionUrl) return;
        mutate({ id: action.id, actionUrl });
        break;
      }
      case 'openTab': {
        const { basicUrl } = /** @type {ApiDashboardOpenTabAction} */ (action);
        if (basicUrl) window.open(normalizeUrl(basicUrl), '_blank');
        closeConfirm();
        break;
      }
      default:
        break;
    }
  }, [mutate, closeConfirm]);

  const handleActionClick = useCallback((/** @type {DashboardActionFlattened} */ action) => {
    if (isPending || !action) return;
    if (action.confirmMessage) {
      setPendingAction(action);
      setConfirmIsOpen(true);
      return;
    }
    executeAction(action);
  }, [isPending, executeAction]);

  const confirmAction = useCallback(() => {
    if (!pendingAction) return;
    executeAction(pendingAction);
  }, [pendingAction, executeAction]);

  return {
    handleActionClick,
    pendingActionId: isPending ? (variables?.id ?? null) : null,
    confirmAction,
    confirmIsOpen,
    closeConfirm,
    confirmMessage: pendingAction?.confirmMessage ?? null,
    isPending,
  };
};

export default useDashboardAction;
